import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '@/types';
import { 
  setAuthToken, 
  setStoredUser, 
  removeAuthToken, 
  removeStoredUser,
  getStoredUser 
} from '@/lib/auth';

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: getStoredUser<User>(),
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
        if (user) {
          setStoredUser(user);
        } else {
          removeStoredUser();
        }
      },

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      login: (user, token) => {
        setAuthToken(token);
        setStoredUser(user);
        set({ user, isAuthenticated: true, isLoading: false, error: null });
      },

      logout: () => {
        removeAuthToken();
        removeStoredUser();
        set({ 
          user: null, 
          isAuthenticated: false, 
          isLoading: false, 
          error: null 
        });
      },

      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = { ...currentUser, ...userData };
          setStoredUser(updatedUser);
          set({ user: updatedUser });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);
